import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable} from 'rxjs';
import {AuthService} from '@service/auth/auth.service';
import {filter, map} from 'rxjs/operators';
import {CrmSection} from '@enum/crm-section';

@Injectable({
  providedIn: 'root'
})
export class UserAreaCrmGuard  {

  constructor(private authService: AuthService, private router: Router) {
  }

  canEnter(sections: ('user-area' | 'crm' | CrmSection)[]): boolean {
    if (sections.includes('user-area')) {
      return this.authService.currentUserPermission.canGoToUserArea;
    } else if (sections.includes('crm')) {
      return this.authService.crmInstalled && this.authService.currentUserPermission.crmSectionsVisible.length > 0;
    } else {
      return this.authService.crmInstalled && this.authService.currentUserPermission.crmSectionsVisible.some((s) => sections.includes(s));
    }
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.authService.isAuthenticated.pipe(
      filter(() => this.authService.currentRole !== null || !this.authService.isUserAuthenticated),
      map(() => {
        if (this.authService.isUserAuthenticated) {
          if (route.data.section && this.canEnter([route.data.section])) {
            return true;
          } else if (route.data.sections && this.canEnter(route.data.sections)) {
            if (!route.data.section) {
              return true;
            } else {
              this.router.navigate(this.getLinkForSections(route.data.sections), {replaceUrl: true}).then();
              return false;
            }
          }
        }
        this.authService.goToLoginPage();
        return false;
      })
    );
  }

  private getLinkForSections(sections: CrmSection[]): string[] {
    const visibleSections = this.authService.currentUserPermission.crmSectionsVisible;
    const firstSection = sections.find((s) => visibleSections.includes(s));
    switch (firstSection) {
      case CrmSection.Lead:
        return ['crm', 'customers', 'lead'];
      case CrmSection.VerifiedContact:
        return ['crm', 'customers', 'verified-contacts'];
      case CrmSection.CustomerList:
        return ['crm', 'customers', 'contact-list'];
      case CrmSection.SalesTasks:
        return ['crm', 'tasks'];
      case CrmSection.Messages:
        return ['crm', 'messages'];
      case CrmSection.Claims:
        return ['crm', 'claims'];
      case CrmSection.Preferences:
        return ['crm', 'preferences'];
    }
    return ['catalog-list'];
  }
}
